import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { formatRegionMoneyMinor } from '@/lib/regionConfig';

const SEARCH_THRESHOLD = 8;

/**
 * One row per filing entity on the review step: who, which accounts, which services, how fast, what it costs.
 * Order-level extras (optional filing services) are listed once under "Services" and priced in the order summary.
 */
const ReviewEntitiesTable = ({ companies, regionConfig, accountTypeNames, serviceNames, financialYear, deliveryTime, quoteByCompany, quoteLoading }) => {
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return companies;
    return companies.filter((c) =>
      c.name?.toLowerCase().includes(term) || c.registrationNumber?.toLowerCase().includes(term));
  }, [companies, search]);

  const isExpress = deliveryTime === 'express';
  const delivery = regionConfig.delivery[isExpress ? 'express' : 'standard'];
  const money = (pence) => formatRegionMoneyMinor(pence, regionConfig);
  const priceCell = (company, field) => {
    const row = quoteByCompany?.[String(company.id)];
    return row ? money(row[field]) : (quoteLoading ? '...' : '-');
  };
  const discountCell = (company) => {
    const row = quoteByCompany?.[String(company.id)];
    if (!row) return quoteLoading ? '...' : '-';
    return row.discount_pence ? `-${money(row.discount_pence)}` : '-';
  };
  const servicesLabel = serviceNames.length > 0 ? serviceNames.join(', ') : '—';

  return (
    <section className="bg-card border border-border rounded-md p-4 mb-4" data-testid="review-entities">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-heading font-medium text-sm">Filing entities ({companies.length})</h3>
        {companies.length > SEARCH_THRESHOLD && (
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search entities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium tracking-wider uppercase text-muted-foreground border-b border-border">
              <th className="py-2 pr-3 w-8">#</th>
              <th className="py-2 pr-3">Entity</th>
              <th className="py-2 pr-3">Account type</th>
              <th className="py-2 pr-3">Services</th>
              <th className="py-2 pr-3">Financial year</th>
              <th className="py-2 pr-3">Turnaround</th>
              <th className="py-2 pr-3 text-right">List price</th>
              <th className="py-2 pr-3 text-right">Discount</th>
              <th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((company, index) => (
              <tr key={company.id} className="border-b border-border last:border-0 align-top">
                <td className="py-3 pr-3 font-mono text-muted-foreground">{index + 1}</td>
                <td className="py-3 pr-3">
                  <p className="font-medium">{company.name}</p>
                  {company.registrationNumber && (
                    <p className="font-mono text-xs text-muted-foreground">{company.registrationNumber}</p>
                  )}
                </td>
                <td className="py-3 pr-3">{accountTypeNames[company.accountType] || '—'}</td>
                <td className="py-3 pr-3 text-muted-foreground">{servicesLabel}</td>
                <td className="py-3 pr-3 font-mono">{financialYear || '—'}</td>
                <td className="py-3 pr-3 whitespace-nowrap">
                  {delivery.shortLabel}
                  {delivery.hours && /^\d+h$/.test(delivery.hours) && (
                    <span className="block text-xs text-muted-foreground">{parseInt(delivery.hours, 10)} hours</span>
                  )}
                </td>
                <td className="py-3 pr-3 text-right font-mono whitespace-nowrap">{priceCell(company, 'subtotal_pence')}</td>
                <td className="py-3 pr-3 text-right font-mono whitespace-nowrap">{discountCell(company)}</td>
                <td className="py-3 text-right font-mono font-medium whitespace-nowrap">{priceCell(company, 'total_pence')}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-muted-foreground">No entities match "{search}"</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ReviewEntitiesTable;
